import Head from 'next/head';
import { getMarketingMetadata } from '~/lib/marketing/seo';
import type { MarketingPagePath } from '~/lib/marketing/seo';

export default function MarketingSeo({
  pathname,
  siteUrl = process.env.NEXT_PUBLIC_SITE_URL,
  environment = process.env.NEXT_PUBLIC_ENVIRONMENT,
}: {
  pathname: MarketingPagePath;
  siteUrl?: string;
  environment?: string;
}) {
  const { title, description, canonical, image, origin } = getMarketingMetadata(
    pathname,
    siteUrl,
  );
  const noindex =
    environment === 'staging' ||
    (origin && new URL(origin).hostname === 'staging.directordeck.com');
  const home = origin ? new URL('/', origin).href : undefined;
  const graph =
    pathname === '/' && home && !noindex
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': home + '#organization',
              name: 'DirectorDeck',
              url: home,
              logo: new URL(
                '/assets/images/directordeck-logo-on-light.png',
                home,
              ).href,
            },
            {
              '@type': 'WebSite',
              '@id': home + '#website',
              name: 'DirectorDeck',
              url: home,
              publisher: { '@id': home + '#organization' },
            },
          ],
        }
      : undefined;
  return (
    <Head>
      <title key="title">{title}</title>
      <link
        key="favicon"
        rel="shortcut icon"
        href="/assets/images/favicon/favicon.ico"
      />
      <link
        key="favicon-svg"
        rel="icon"
        type="image/svg+xml"
        href="/assets/images/favicon/favicon.svg"
      />
      <link
        key="apple-touch-icon"
        rel="apple-touch-icon"
        sizes="180x180"
        href="/assets/images/favicon/apple-touch-icon.png"
      />
      <link
        key="favicon-16"
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/assets/images/favicon/favicon-16x16.png"
      />
      <link
        key="favicon-32"
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/assets/images/favicon/favicon-32x32.png"
      />
      <link
        key="manifest"
        rel="manifest"
        href="/assets/images/favicon/site.webmanifest"
      />
      <link
        key="mask-icon"
        rel="mask-icon"
        href="/assets/images/favicon/safari-pinned-tab.svg"
        color="#524cc8"
      />
      <meta
        key="msapplication-TileColor"
        name="msapplication-TileColor"
        content="#08081c"
      />
      <meta
        key="msapplication-config"
        name="msapplication-config"
        content="/assets/images/favicon/browserconfig.xml"
      />
      <meta key="description" name="description" content={description} />
      {canonical && <link key="canonical" rel="canonical" href={canonical} />}
      <meta key="og:type" property="og:type" content="website" />
      <meta key="og:site_name" property="og:site_name" content="DirectorDeck" />
      <meta key="og:title" property="og:title" content={title} />
      <meta
        key="og:description"
        property="og:description"
        content={description}
      />
      {canonical && <meta key="og:url" property="og:url" content={canonical} />}
      {image && <meta key="og:image" property="og:image" content={image} />}
      {image && (
        <meta
          key="og:image:alt"
          property="og:image:alt"
          content="A presentation created with DirectorDeck"
        />
      )}
      <meta
        key="twitter:card"
        name="twitter:card"
        content="summary_large_image"
      />
      <meta key="twitter:title" name="twitter:title" content={title} />
      <meta
        key="twitter:description"
        name="twitter:description"
        content={description}
      />
      {image && (
        <meta key="twitter:image" name="twitter:image" content={image} />
      )}
      {noindex && (
        <meta key="robots" name="robots" content="noindex, nofollow" />
      )}
      {graph && (
        <script
          key="marketing-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(graph).replace(/</g, '\\u003c'),
          }}
        />
      )}
    </Head>
  );
}
